import { useEffect, useRef, type RefObject } from "react";

interface UseInfiniteScrollOptions {
  enabled?: boolean;
  rootMargin?: string;
  /** Re-attach the observer when layout/content changes (e.g. loaded row count). */
  rootKey?: string | number;
}

export function useInfiniteScroll(
  sentinelRef: RefObject<Element | null>,
  onLoadMore: () => void,
  { enabled = true, rootMargin = "200px", rootKey }: UseInfiniteScrollOptions = {},
): void {
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  useEffect(() => {
    if (!enabled) return;

    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onLoadMoreRef.current();
        }
      },
      { rootMargin },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [enabled, rootMargin, rootKey, sentinelRef]);
}
